import React from 'react'

import classNames from 'classnames'
import PropTypes from 'prop-types'

import { formPropTypes } from '@/common/propTypes'
import { traverseObject } from '@/common/utils'

import styling from './formSelect.module.scss'

/**
 * The select component allows the user to pick one item from a preset collection of options
 */
const FormSelect = React.memo(
  ({
    children,
    className,
    disabled = false,
    errors,
    errorTextProp,
    hintContent,
    invalid: invalidProp = false,
    innerRef,
    isLabelInset = false,
    label,
    name,
    readonly = false,
    register,
    required,
    size,
    valid = false,
    validationErrors,
    ...props
  }) => {
    const makeDisabled = disabled || readonly // readonly is not supported for select, so use disabled
    const invalid =
      !!traverseObject(errors, name) || !!traverseObject(validationErrors, name) || invalidProp
    const errorText =
      traverseObject(errors, name)?.message ||
      traverseObject(validationErrors, name) ||
      errorTextProp

    const selectClasses = classNames(
      'form-control',
      styling['form-select'],
      size && styling[`form-select-${size}`],
      valid && 'is-valid',
      invalid && 'is-invalid',
      disabled && 'disabled',
      'mt-3'
    )

    const fieldsetClasses = classNames(
      'form-control',
      size && styling[`form-select-${size}`],
      valid && 'is-valid',
      invalid && 'is-invalid',
      disabled && 'disabled'
    )

    const containerClasses = classNames(className, disabled && 'disabled', 'form-control-container')

    const renderSelect = () => {
      return (
        <select
          {...props}
          className={selectClasses}
          disabled={makeDisabled}
          readOnly={readonly}
          ref={innerRef}
          {...register?.(name || label, { required })}
        >
          {children}
        </select>
      )
    }

    return (
      <div className={containerClasses}>
        {label && !isLabelInset && (
          <label className={`${size ? `form-control-label-${size}` : ''}`} htmlFor={props.id}>
            {label}
            {required && '*'}
          </label>
        )}
        {hintContent && label ? <div className='form-control-hint'>{hintContent}</div> : []}

        {isLabelInset ? (
          <fieldset className={fieldsetClasses} tabIndex={-1}>
            <legend>
              {label}
              {required && '*'}
            </legend>
            {renderSelect()}
          </fieldset>
        ) : (
          renderSelect()
        )}

        <div className='form-control-descenders'>
          <div>
            {invalid && errorText ? <div className='form-control-error'>{errorText}</div> : []}
            {hintContent && !label ? <div className='form-control-hint'>{hintContent}</div> : []}
          </div>
          {required && !label && !invalid && <div className='form-control-required'>Required</div>}
        </div>
      </div>
    )
  }
)

FormSelect.propTypes = {
  ...formPropTypes,

  /**
   * The options of the control, as children nodes.
   */
  children: PropTypes.node,
  /**
   * Validation errors object from the front-end
   */
  errors: PropTypes.object,
  /**
   * The inner ref.
   */
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.string]),

  /**
   * If the Label should appear inside the input's border.
   */
  isLabelInset: PropTypes.bool,
  /**
   * Whether or not the input is readonly
   */
  readonly: PropTypes.bool,

  /**
   * The input's size.
   */
  size: PropTypes.string,
  /**
   * Validation errors from our API
   */
  validationErrors: PropTypes.object,
}

export default FormSelect
